import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { DocumentDto } from '../models/document-dto';
import { DocumentsApiService } from '../services/documents-api.service';
import { Injectable, inject } from '@angular/core';
import { DocumentsStateModel } from '../models/documents-state.model';
import { Document } from '../models/document.model';
import { environment } from 'src/environments/environment';
import { map, Observable, switchMap, tap } from 'rxjs';
import { LoadDownloads, LoadForms, LoadPhlebotomy } from './documents.actions';
import { OpenModal, ShowDownloadsModal, ShowFormsModal, ShowPhlebotomyModal } from '../../../app/states/actions/modal.actions';
import { DocumentsComponent } from '../components/documents/documents.component';
import { DownloadsModalComponent } from '../components/downloads-modal/downloads-modal.component';
import { DownloadGroup } from '../models/downloads-modal-config';

@State<DocumentsStateModel>({
  name: 'documentsState',
  defaults: {
    phlebotomy: [],
    forms: [],
    downloads: []
  }
})
@Injectable()
export class DocumentsState {
  private readonly api = inject(DocumentsApiService);
  private readonly store = inject(Store);

  @Selector([DocumentsState.phlebotomy])
  public static phlebotomyModels(documents: DocumentDto[]): Document[] {
    return documents.map((documentDto: DocumentDto) => {
      return DocumentsState.documentDtoToDocument(documentDto);
    });
  }

  @Selector([DocumentsState.forms])
  public static formsModels(documents: DocumentDto[]): Document[] {
    return documents.map((documentDto: DocumentDto) => {
      return DocumentsState.documentDtoToDocument(documentDto);
    });
  }

  @Selector()
  public static phlebotomy(state: DocumentsStateModel): DocumentDto[] {
    return state.phlebotomy;
  }

  @Selector()
  public static forms(state: DocumentsStateModel): DocumentDto[] {
    return state.forms;
  }

  public static documentDtoToDocument(documentDto: DocumentDto): Document {
    const filePath = documentDto.filePath.replace(/\\/g, '/');
    let url = environment.idxUrl + filePath + documentDto.fileName;
    if (filePath.startsWith('http')) {
      url = filePath + documentDto.fileName;
    }
    return {
      name: documentDto.name,
      url
    };
  }

  @Action(LoadPhlebotomy)
  public loadPhlebotomy(ctx: StateContext<DocumentsStateModel>): Observable<DocumentDto[]> {
    return this.api.getPhlebotomyDocuments().pipe(
      tap((documents: DocumentDto[]) => {
        ctx.patchState({ phlebotomy: documents });
      })
    );
  }

  @Action(LoadForms)
  public loadForms(ctx: StateContext<DocumentsStateModel>): Observable<DocumentDto[]> {
    return this.api.getForms().pipe(
      tap((documents: DocumentDto[]) => {
        ctx.patchState({ forms: documents });
      })
    );
  }

  @Action(ShowPhlebotomyModal)
  public openPhlebotomyModal(ctx: StateContext<DocumentsStateModel>): Observable<void> {
    return this.store.dispatch(new LoadPhlebotomy()).pipe(
      map(() => this.store.selectSnapshot(DocumentsState.phlebotomyModels)),
      switchMap(documents => {
        return ctx.dispatch(
          new OpenModal<DocumentsComponent>(DocumentsComponent, {
            data: {
              header: 'Phlebotomy Documents',
              documents
            }
          })
        );
      })
    );
  }

  @Action(ShowFormsModal)
  public openFormsModal(ctx: StateContext<DocumentsStateModel>): Observable<void> {
    return this.store.dispatch(new LoadForms()).pipe(
      map(() => this.store.selectSnapshot(DocumentsState.formsModels)),
      switchMap(documents => {
        return ctx.dispatch(
          new OpenModal<DocumentsComponent>(DocumentsComponent, {
            data: {
              header: 'Forms',
              documents
            }
          })
        );
      })
    );
  }

  @Selector()
  public static downloads(state: DocumentsStateModel): DocumentDto[] {
    return state.downloads;
  }

  @Selector([DocumentsState.downloads])
  public static downloadsGroups(documents: DocumentDto[]): DownloadGroup[] {
    const groupMap = new Map<string, Document[]>();
    for (const doc of documents) {
      const groupName = doc.groupName || 'Other';
      if (!groupMap.has(groupName)) {
        groupMap.set(groupName, []);
      }
      const group = groupMap.get(groupName);
      if (group) {
        group.push(DocumentsState.documentDtoToDocument(doc));
      }
    }
    return Array.from(groupMap.entries()).map(([name, links]) => ({
      name,
      links: links.sort((a, b) => a.name.localeCompare(b.name)),
      instructions: DocumentsState.getGroupInstructions(name)
    }));
  }

  private static getGroupInstructions(groupName: string): string[] {
    if (groupName === 'AutoConnect') {
      return [
        'Click the link to download and run setup.',
        'Follow the steps on the setup wizard.',
        'Please complete the blank part of the URL, when asked during setup. [e.g. https://xxx.insightdx.com. You need to enter the appropriate name for xxx here.]',
        'After successful installation, the application starts up automatically and you will be asked to select the default printer. Please select the printer and save.',
        'You will be prompted with the Autoprint login screen. Login with the credentials to start the AutoPrint application.'
      ];
    }
    return [
      'Click the link to download and run setup.',
      'Follow the steps on the setup wizard.',
      'Follow instructions from the User Guide for setup & use.'
    ];
  }

  @Action(LoadDownloads)
  public loadDownloads(ctx: StateContext<DocumentsStateModel>): Observable<DocumentDto[]> {
    return this.api.getPrinterDownloads().pipe(
      tap((documents: DocumentDto[]) => {
        ctx.patchState({ downloads: documents });
      })
    );
  }

  @Action(ShowDownloadsModal)
  public openDownloadsModal(ctx: StateContext<DocumentsStateModel>): Observable<void> {
    return this.store.dispatch(new LoadDownloads()).pipe(
      map(() => this.store.selectSnapshot(DocumentsState.downloadsGroups)),
      switchMap(groups => ctx.dispatch(new OpenModal<DownloadsModalComponent>(DownloadsModalComponent, { data: { groups } })))
    );
  }
}
